/**
 * Build a weld map: vertex index -> canonical representative index.
 * Vertices at the same position, within epsilon, share the same representative.
 */
export function buildWeldReps(
  positions: Float32Array,
  epsilon = 1e-5,
): Int32Array {
  const vertexCount = positions.length / 3;
  const reps = new Int32Array(vertexCount);
  const invCell = 1 / epsilon;
  const eps2 = epsilon * epsilon;

  const cells = new Map<string, number[]>();
  for (let i = 0; i < vertexCount; i++) {
    const key = `${Math.floor(positions[i * 3] * invCell)},${Math.floor(positions[i * 3 + 1] * invCell)},${Math.floor(positions[i * 3 + 2] * invCell)}`;
    let cell = cells.get(key);
    if (!cell) {
      cell = [];
      cells.set(key, cell);
    }
    cell.push(i);
  }

  reps.fill(-1);
  for (let i = 0; i < vertexCount; i++) {
    if (reps[i] >= 0) continue;
    reps[i] = i;
    const ix = positions[i * 3];
    const iy = positions[i * 3 + 1];
    const iz = positions[i * 3 + 2];
    const cx = Math.floor(ix * invCell);
    const cy = Math.floor(iy * invCell);
    const cz = Math.floor(iz * invCell);
    for (let dx = -1; dx <= 1; dx++) {
      for (let dy = -1; dy <= 1; dy++) {
        for (let dz = -1; dz <= 1; dz++) {
          const cell = cells.get(`${cx + dx},${cy + dy},${cz + dz}`);
          if (!cell) continue;
          for (const j of cell) {
            if (j <= i || reps[j] >= 0) continue;
            const ddx = positions[j * 3] - ix;
            const ddy = positions[j * 3 + 1] - iy;
            const ddz = positions[j * 3 + 2] - iz;
            if (ddx * ddx + ddy * ddy + ddz * ddz <= eps2) reps[j] = i;
          }
        }
      }
    }
  }
  return reps;
}
